import type { Facility, GroupForExport, TimetableStructure } from '@schooly/api';
import { FacilityType, GroupType, PeriodTypeEnum } from '@schooly/api';
import type { ColumnSchema } from '@schooly/components/xls-service';
import {
  forbiddenPairs,
  matchesAny,
  matchesTiered,
  noDuplicates,
  required,
} from '@schooly/components/xls-service';

import type { TimetableImportMessages } from './useTimetableImportMessages';

const INTERNAL_KEYS = ['day', 'period', 'group', 'room'] as const;

export function buildColumnMapping(headers: string[]) {
  return Object.fromEntries(headers.map((h, i) => [h, INTERNAL_KEYS[i]]));
}

export function normalizeRoomPath(value: string): string {
  return value
    .split(';')
    .map((s) => s.trim().toLowerCase())
    .join(';');
}

export function buildRoomPathMap(facilities: Facility[]): Map<string, string> {
  const byId = new Map(facilities.map((f) => [f.id, f]));
  const pathMap = new Map<string, string>();

  for (const f of facilities) {
    if (f.type !== FacilityType.Room) continue;

    const parts: string[] = [f.name];
    let current = f.parent_id ? byId.get(f.parent_id) : null;

    while (current) {
      parts.unshift(current.name);
      current = current.parent_id ? byId.get(current.parent_id) : null;
    }

    pathMap.set(normalizeRoomPath(parts.join(';')), f.id);
  }

  return pathMap;
}

export type GroupWithCode = GroupForExport & { timetable_code: string };

export const isGroupWithCode = (group: GroupForExport): group is GroupWithCode =>
  group.timetable_code !== null;

export type BuildTimetableSchemaParams = {
  msg: TimetableImportMessages;
  structure: TimetableStructure;
  groups: GroupWithCode[];
  roomPathMap: Map<string, string>;
};

export function buildTimetableSchema({
  msg,
  structure,
  groups,
  roomPathMap,
}: BuildTimetableSchemaParams) {
  const structureDays = structure.weekdays.map((_, i) => String(i + 1));

  const allPeriodIndices = new Set(structure.periods.map((_, i) => String(i + 1)));
  const allowedPeriodIndices = new Set(
    structure.periods
      .map((p, i) => [p.type, String(i + 1)] as const)
      .filter(([type]) => type === PeriodTypeEnum.Lesson || type === PeriodTypeEnum.Registration)
      .map(([, idx]) => idx),
  );

  const structureAgeGroups = new Set(structure.age_group_ids);
  const allGroupCodes = new Set(groups.map((g) => g.timetable_code));
  const codesInStructure = new Set(
    groups
      .filter((g) => g.age_groups.some((ag) => structureAgeGroups.has(ag.id)))
      .map((g) => g.timetable_code),
  );

  const groupTypes = new Map<string, unknown>(groups.map((g) => [g.timetable_code, g.group_type]));
  const periodTypes = new Map<string, unknown>(
    structure.periods.map((p, i) => [String(i + 1), p.type]),
  );

  const schema: ColumnSchema[] = [
    {
      column: 'day',
      rules: [required(msg.dayValueEmpty), matchesAny(structureDays, msg.dayNotExist)],
    },
    {
      column: 'period',
      rules: [
        required(msg.periodValueEmpty),
        matchesTiered(
          allPeriodIndices,
          msg.periodNotExist,
          allowedPeriodIndices,
          msg.groupAllocationNotPossible,
        ),
      ],
    },
    {
      column: 'group',
      rules: [
        required(msg.groupValueEmpty),
        matchesTiered(
          allGroupCodes,
          msg.groupIsNotFound,
          codesInStructure,
          msg.groupAgeGroupMismatch,
        ),
      ],
    },
    {
      column: 'room',
      warnRules: [
        (value) => {
          const str = normalizeRoomPath(String(value ?? ''));
          if (!str) return null;
          return roomPathMap.has(str) ? null : msg.roomNotExist;
        },
      ],
    },
  ];

  const crossRules = [
    noDuplicates(['group', 'day', 'period'], msg.duplicateLesson),
    forbiddenPairs({
      columnA: 'period',
      resolverA: periodTypes,
      columnB: 'group',
      resolverB: groupTypes,
      pairs: [
        [PeriodTypeEnum.Registration, GroupType.Subject, msg.subjectInRegistration],
        [PeriodTypeEnum.Lesson, GroupType.TutorGroup, msg.tutorInLesson],
      ],
    }),
  ];

  return { schema, crossRules };
}
